import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, NotificationOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { get, post, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './AdConfigPage.css';

type AdProvider = 'none' | 'csj' | 'gdt' | 'admob' | 'custom';
type AdPlatform = 'android' | 'ios' | 'web';
type AdType = 'banner' | 'splash' | 'interstitial' | 'rewarded' | 'native';

interface AdPlacement {
  key: string;
  name: string;
  scene: string;
  ad_type: AdType;
  platforms: AdPlatform[];
  android_unit_id: string;
  ios_unit_id: string;
  web_unit_id: string;
  enabled: boolean;
  daily_limit: number;
  interval_seconds: number;
  reward_name: string;
  reward_amount: number;
  sort: number;
}

interface AdConfig {
  config_key: string;
  enabled: boolean;
  provider: AdProvider;
  test_mode: boolean;
  personalized_ads: boolean;
  android_app_id: string;
  ios_app_id: string;
  web_app_id: string;
  privacy_url: string;
  notes: string;
  placements: AdPlacement[];
  version: number;
  updated_by: string;
  updated_at?: string;
  polaris_ads_enabled: boolean;
  polaris_base_url: string;
  polaris_app_id: string;
  polaris_app_secret: string;
  polaris_position: string;
}

const providerOptions = [
  { value: 'none', label: '暂不接入' },
  { value: 'csj', label: '穿山甲 / GroMore' },
  { value: 'gdt', label: '腾讯优量汇' },
  { value: 'admob', label: 'Google AdMob' },
  { value: 'custom', label: '自定义广告服务' },
];

const platformOptions = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
  { value: 'web', label: 'Web / H5' },
];

const adTypeOptions = [
  { value: 'banner', label: '横幅 Banner' },
  { value: 'splash', label: '开屏 Splash' },
  { value: 'interstitial', label: '插屏 Interstitial' },
  { value: 'rewarded', label: '激励视频 Rewarded' },
  { value: 'native', label: '原生信息流 Native' },
];

const emptyPlacement = (): AdPlacement => ({
  key: `custom_ad_${Date.now()}`,
  name: '新广告位',
  scene: 'custom',
  ad_type: 'banner',
  platforms: ['android', 'ios'],
  android_unit_id: '',
  ios_unit_id: '',
  web_unit_id: '',
  enabled: false,
  daily_limit: 0,
  interval_seconds: 0,
  reward_name: '',
  reward_amount: 0,
  sort: 100,
});

const AdConfigPage: React.FC = () => {
  const [form] = Form.useForm<AdConfig>();
  const [config, setConfig] = useState<AdConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingPolaris, setTestingPolaris] = useState(false);
  const watchedPlacements = Form.useWatch('placements', form) || [];
  const enabledCount = watchedPlacements.filter((item) => item?.enabled).length;

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<AdConfig>(API_ENDPOINTS.ad_config_app.config);
      setConfig(data);
      form.setFieldsValue(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载广告配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const updated = await put<AdConfig>(API_ENDPOINTS.ad_config_app.config, values);
      setConfig(updated);
      form.setFieldsValue(updated);
      message.success('广告配置已保存，新配置已供客户端读取');
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePolarisTest = async () => {
    setTestingPolaris(true);
    try {
      const result = await post<{ success: boolean; message: string; positions: string[] }>(API_ENDPOINTS.ad_config_app.polarisTest);
      result.success ? message.success(result.message) : message.error(result.message);
      if (result.positions?.length) message.info(`已发现广告位：${result.positions.join('、')}`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '连接测试失败');
    } finally {
      setTestingPolaris(false);
    }
  };

  const handleClearCache = async () => {
    const result = await post<{ message: string }>(API_ENDPOINTS.ad_config_app.polarisClearCache);
    message.success(result.message);
  };

  return (
    <div className="ad-config-page">
      <div className="ad-config-header">
        <div>
          <h1 className="page-title">广告配置</h1>
          <p>预先维护广告平台与广告位。只有总开关和广告位同时启用后，客户端才会收到可用配置。</p>
        </div>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          保存配置
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        message="北极星展示广告已接入"
        description="H5 只访问声屿服务端 GET /api/v1/ads，App Secret 不会发送到浏览器。北极星不可用时自动使用缓存或隐藏广告，不影响首页。"
      />

      <Spin spinning={loading}>
        {config && (
          <Space wrap className="ad-config-summary">
            <Tag color={config.enabled ? 'green' : 'default'}>{config.enabled ? '广告已开启' : '广告总开关关闭'}</Tag>
            <Tag color={config.test_mode ? 'orange' : 'blue'}>{config.test_mode ? '测试模式' : '正式模式'}</Tag>
            <Tag color="purple">配置版本 v{config.version}</Tag>
            <Tag>{enabledCount} 个广告位启用</Tag>
            {config.updated_at && <Tag>最近更新：{new Date(config.updated_at).toLocaleString()}</Tag>}
          </Space>
        )}

        <Form
          form={form}
          layout="vertical"
          initialValues={{ enabled: false, provider: 'none', test_mode: true, personalized_ads: false, placements: [] }}
        >
          <Card
            title="北极星展示广告"
            className="ad-config-card"
            extra={<Space><Button loading={testingPolaris} onClick={handlePolarisTest}>测试连接</Button><Button onClick={handleClearCache}>清除缓存</Button></Space>}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} md={6}><Form.Item label="启用 H5 展示广告" name="polaris_ads_enabled" valuePropName="checked"><Switch /></Form.Item></Col>
              <Col xs={24} md={18}><Form.Item label="北极星 Base URL" name="polaris_base_url" rules={[{ type: 'url', warningOnly: true }]}><Input placeholder="https://polaris.example.com（不要填写 /api/v1）" /></Form.Item></Col>
              <Col xs={24} md={8}><Form.Item label="App ID" name="polaris_app_id"><Input /></Form.Item></Col>
              <Col xs={24} md={8}><Form.Item label="App Secret" name="polaris_app_secret"><Input.Password placeholder="已保存时显示 ********" autoComplete="new-password" /></Form.Item></Col>
              <Col xs={24} md={8}><Form.Item label="H5 广告位 position" name="polaris_position" rules={[{ required: true }, { pattern: /^[a-z][a-z0-9_]*$/, message: '使用小写字母、数字和下划线' }]}><Input placeholder="h5_radio_home_banner" /></Form.Item></Col>
            </Row>
            <Typography.Paragraph type="secondary">保存后点击“测试连接”。App Secret 仅保存在服务端数据库，管理端回显会自动掩码。</Typography.Paragraph>
          </Card>

          <Card title="全局设置" className="ad-config-card">
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} lg={6}>
                <Form.Item label="广告总开关" name="enabled" valuePropName="checked">
                  <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={6}>
                <Form.Item label="测试模式" name="test_mode" valuePropName="checked">
                  <Switch checkedChildren="测试" unCheckedChildren="正式" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={6}>
                <Form.Item label="个性化广告" name="personalized_ads" valuePropName="checked">
                  <Switch checkedChildren="允许" unCheckedChildren="关闭" />
                </Form.Item>
              </Col>
              <Col xs={24} lg={6}>
                <Form.Item label="广告平台" name="provider" rules={[{ required: true, message: '请选择广告平台' }]}>
                  <Select options={providerOptions} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col xs={24} lg={8}><Form.Item label="Android App ID" name="android_app_id"><Input placeholder="广告平台分配的 Android App ID" /></Form.Item></Col>
              <Col xs={24} lg={8}><Form.Item label="iOS App ID" name="ios_app_id"><Input placeholder="广告平台分配的 iOS App ID" /></Form.Item></Col>
              <Col xs={24} lg={8}><Form.Item label="Web App ID" name="web_app_id"><Input placeholder="Web/H5 广告应用 ID" /></Form.Item></Col>
            </Row>

            <Form.Item
              label="隐私政策地址"
              name="privacy_url"
              rules={[{ type: 'url', warningOnly: true, message: '请输入完整的 HTTP/HTTPS 地址' }]}
            >
              <Input placeholder="https://example.com/privacy" />
            </Form.Item>
            <Form.Item label="配置备注" name="notes">
              <Input.TextArea rows={2} placeholder="记录广告平台账号、上线计划或联调说明；请勿填写平台密钥。" />
            </Form.Item>
          </Card>

          <Card
            title={<Space><NotificationOutlined />广告位管理</Space>}
            className="ad-config-card"
            extra={<Typography.Text type="secondary">0 表示不限制次数或间隔</Typography.Text>}
          >
            <Form.List name="placements">
              {(fields, { add, remove }) => (
                <div className="ad-placement-list">
                  {fields.map((field, index) => {
                    const placement = watchedPlacements[index];
                    return (
                      <Card
                        key={field.key}
                        size="small"
                        className="ad-placement-card"
                        title={
                          <Space wrap>
                            <span>{placement?.name || `广告位 ${index + 1}`}</span>
                            <Tag color={placement?.enabled ? 'green' : 'default'}>{placement?.enabled ? '启用' : '停用'}</Tag>
                            {placement?.ad_type && <Tag color="blue">{adTypeOptions.find((item) => item.value === placement.ad_type)?.label}</Tag>}
                          </Space>
                        }
                        extra={<Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(field.name)}>删除</Button>}
                      >
                        <Row gutter={[16, 0]}>
                          <Col xs={24} md={6}>
                            <Form.Item label="广告位编码" name={[field.name, 'key']} rules={[{ required: true }, { pattern: /^[a-z][a-z0-9_]*$/, message: '使用小写字母、数字和下划线' }]}>
                              <Input placeholder="home_banner" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}><Form.Item label="展示名称" name={[field.name, 'name']} rules={[{ required: true }]}><Input /></Form.Item></Col>
                          <Col xs={24} md={6}><Form.Item label="业务场景" name={[field.name, 'scene']} rules={[{ required: true }]}><Input placeholder="home" /></Form.Item></Col>
                          <Col xs={24} md={6}><Form.Item label="广告类型" name={[field.name, 'ad_type']} rules={[{ required: true }]}><Select options={adTypeOptions} /></Form.Item></Col>
                        </Row>

                        <Row gutter={[16, 0]}>
                          <Col xs={24} lg={9}><Form.Item label="投放平台" name={[field.name, 'platforms']} rules={[{ required: true, message: '请选择至少一个平台' }]}><Select mode="multiple" options={platformOptions} /></Form.Item></Col>
                          <Col xs={12} lg={5}><Form.Item label="启用广告位" name={[field.name, 'enabled']} valuePropName="checked"><Switch /></Form.Item></Col>
                          <Col xs={12} lg={5}><Form.Item label="每日上限" name={[field.name, 'daily_limit']}><InputNumber min={0} max={1000} addonAfter="次" /></Form.Item></Col>
                          <Col xs={12} lg={5}><Form.Item label="最小间隔" name={[field.name, 'interval_seconds']}><InputNumber min={0} max={86400} addonAfter="秒" /></Form.Item></Col>
                        </Row>

                        <Row gutter={[16, 0]}>
                          <Col xs={24} lg={8}><Form.Item label="Android 广告单元 ID" name={[field.name, 'android_unit_id']}><Input /></Form.Item></Col>
                          <Col xs={24} lg={8}><Form.Item label="iOS 广告单元 ID" name={[field.name, 'ios_unit_id']}><Input /></Form.Item></Col>
                          <Col xs={24} lg={8}><Form.Item label="Web 广告单元 ID" name={[field.name, 'web_unit_id']}><Input /></Form.Item></Col>
                        </Row>

                        <Row gutter={[16, 0]}>
                          <Col xs={24} md={8}><Form.Item label="激励名称" name={[field.name, 'reward_name']}><Input placeholder="仅激励视频需要，例如墨水" /></Form.Item></Col>
                          <Col xs={24} md={8}><Form.Item label="激励数量" name={[field.name, 'reward_amount']}><InputNumber min={0} max={100000} /></Form.Item></Col>
                          <Col xs={24} md={8}><Form.Item label="排序" name={[field.name, 'sort']}><InputNumber min={0} max={10000} /></Form.Item></Col>
                        </Row>
                      </Card>
                    );
                  })}

                  <Button block type="dashed" icon={<PlusOutlined />} onClick={() => add(emptyPlacement())}>
                    新增广告位
                  </Button>
                </div>
              )}
            </Form.List>
          </Card>
        </Form>
      </Spin>
    </div>
  );
};

export default AdConfigPage;
